import { Map, OrderedSet } from 'immutable';

import { getOSName, getBrowserName, getDeviceName, getFrameworkName } from '../../../util-platform';
import { TrendsType } from '../../../constants';
import { calculatePercentage } from '../../../util';

export const SUMMARY_AGGREGATION_TYPES = [
  {
    name: TrendsType.BROWSER,
    titleParser: getBrowserName,
  },
  {
    name: TrendsType.OS,
    titleParser: getOSName,
  },
  {
    name: TrendsType.DEVICE,
    titleParser: getDeviceName,
    useParsedName: true,
  },
  {
    name: TrendsType.FRAMEWORK,
    titleParser: getFrameworkName,
  },
];

export const getInitAggregatedNames = () => (
  Map().withMutations((updated) => {
    SUMMARY_AGGREGATION_TYPES.reduce((map, { name }) => (
      map.set(name, OrderedSet())
    ), updated);
  })
);

export const clearSelectedVersion = (state) => state.set(
  'selectedVersion',
  Map({ title: null, items: [] }),
);

export const consolidateMetric = (data, mainKey, errorKey, failKey) => {
  if (!data[mainKey]) {
    return {};
  }

  const metric = Object.entries(data[mainKey]).reduce(
    (obj, [name, count]) => ({ ...obj, [name]: { value: count, error: 0, fail: 0 } }),
    {},
  );

  // data[errorKey] can be undefined
  Object.entries(data[errorKey] || {}).forEach(([name, count]) => {
    if (name in metric) {
      metric[name].error += count;
    }
  });

  // data[failKey] can be undefined
  Object.entries(data[failKey] || {}).forEach(([name, count]) => {
    if (name in metric) {
      metric[name].fail += count;
    }
  });

  return metric;
};

export const aggregatePlatformMetrics = (nameToMetricsMap, nameFn, useParsedName = false) => (
  Object
    .entries(nameToMetricsMap)
    .reduce((acc, [title, metrics]) => {
      const name = nameFn(title);

      if (!(name in acc)) {
        acc[name] = { value: 0, fail: 0, error: 0, failRate: 0, errorRate: 0, items: [] };
      }

      acc[name].value += metrics.value;
      acc[name].fail += metrics.fail;
      acc[name].error += metrics.error;
      acc[name].failRate = calculatePercentage(acc[name].fail, acc[name].value);
      acc[name].errorRate = calculatePercentage(acc[name].error, acc[name].value);
      acc[name].items.push({
        title: useParsedName ? name : title,
        ...metrics,
        failRate: calculatePercentage(metrics.fail, metrics.value),
        errorRate: calculatePercentage(metrics.error, metrics.value),
      });

      return acc;
    }, {})
);

export const deleteUnknownMetrics = (metrics) => {
  const modifiedMetrics = { ...metrics };
  if (metrics?.device?.['']) {
    modifiedMetrics.device.desktop = (metrics.device.desktop || 0) + metrics.device[''];
  }
  if (metrics?.deviceError?.['']) {
    modifiedMetrics.deviceError.desktop = (metrics.deviceError.desktop || 0) + metrics.deviceError[''];
  }
  if (metrics?.deviceFail?.['']) {
    modifiedMetrics.deviceFail.desktop = (metrics.deviceFail.desktop || 0) + metrics.deviceFail[''];
  }

  SUMMARY_AGGREGATION_TYPES.forEach(({ name }) => {
    const errorKey = `${name}Error`;
    const failKey = `${name}Fail`;
    if (metrics?.[name]?.['']) {
      delete modifiedMetrics[name][''];
    }
    if (metrics?.[name]?.[' ']) {
      delete modifiedMetrics[name][' '];
    }
    if (metrics?.[errorKey]?.['']) {
      delete modifiedMetrics[errorKey][''];
    }
    if (metrics?.[errorKey]?.[' ']) {
      delete modifiedMetrics[errorKey][' '];
    }
    if (metrics?.[failKey]?.['']) {
      delete modifiedMetrics[failKey][''];
    }
    if (metrics?.[failKey]?.[' ']) {
      delete modifiedMetrics[failKey][' '];
    }
  });

  return modifiedMetrics;
};

export const parseAggregatedMetrics = (newState, rawMetrics) => {
  const metrics = deleteUnknownMetrics(rawMetrics);
  SUMMARY_AGGREGATION_TYPES.forEach(({ name, titleParser, useParsedName }) => {
    const consolidatedData = consolidateMetric(metrics, name, `${name}Error`, `${name}Fail`);
    const names = aggregatePlatformMetrics(consolidatedData, titleParser, useParsedName);
    newState.setIn(
      ['aggregatedNames', name],
      OrderedSet(
        Object
          .entries(names)
          .map(([title, aggs]) => ({ title, ...aggs }))
          .sort((item1, item2) => item1.value - item2.value),
      ),
    );
  });
};
